import React, { useState, useRef, useCallback } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import '../../styles/tiptap-editor.css'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import { Color } from '@tiptap/extension-color'
import TextStyle from '@tiptap/extension-text-style'
import Highlight from '@tiptap/extension-highlight'
// Removing problematic imports temporarily - will add back working ones
// import Table from '@tiptap/extension-table'
// import TableRow from '@tiptap/extension-table-row'
// import TableHeader from '@tiptap/extension-table-header'
// import TableCell from '@tiptap/extension-table-cell'
// import TaskList from '@tiptap/extension-task-list'
// import TaskItem from '@tiptap/extension-task-item'
// import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
// import { createLowlight } from 'lowlight'
// import Subscript from '@tiptap/extension-subscript'
// import Superscript from '@tiptap/extension-superscript'
// import Strike from '@tiptap/extension-strike'
// import FontFamily from '@tiptap/extension-font-family'
// import CharacterCount from '@tiptap/extension-character-count'
// import Placeholder from '@tiptap/extension-placeholder'
// import Youtube from '@tiptap/extension-youtube'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CircularProgress, Alert } from '@mui/material'
import { getAllPosts, createPost, updatePost, deletePost } from '../../api/posts'
import Pagination from '../Paginations'

const AdminPosts = () => {
    const [currentPage, setCurrentPage] = useState(1)
    const [isCreateMode, setIsCreateMode] = useState(true)
    const [editingPost, setEditingPost] = useState(null)
    const [formData, setFormData] = useState({
        title: '',
        excerpt: '',
        featured_image: '',
        slug: '',
        status: 'published',
        tags: []
    })
    const [newTag, setNewTag] = useState('')
    const [error, setError] = useState('')
    const successBtn = useRef()
    const queryClient = useQueryClient()

    // Get admin user ID from localStorage
    const adminUserId = localStorage.getItem('aid')

    const [isFullscreen, setIsFullscreen] = useState(false)
    const [activeColorTool, setActiveColorTool] = useState(null)

    // Configure Tiptap editor with working extensions
    const editor = useEditor({
        extensions: [
            StarterKit,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'link-style',
                },
            }),
            Image.configure({
                HTMLAttributes: {
                    class: 'editor-image',
                },
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Underline,
            TextStyle,
            Color.configure({
                types: ['textStyle'],
            }),
            Highlight.configure({
                multicolor: true,
                HTMLAttributes: {
                    class: 'highlight-style',
                },
            }),
        ],
        content: '<p></p>',
        editorProps: {
            attributes: {
                class: 'modern-editor-content',
            },
        },
    })

    // Editor utility functions
    const addImage = useCallback(() => {
        const url = window.prompt('Enter image URL:')
        if (url) {
            editor?.chain().focus().setImage({ src: url }).run()
        }
    }, [editor])

    const addLink = useCallback(() => {
        const previousUrl = editor?.getAttributes('link').href
        const url = window.prompt('URL', previousUrl)
        
        if (url === null) {
            return
        }
        
        if (url === '') {
            editor?.chain().focus().extendMarkRange('link').unsetLink().run()
            return
        }
        
        editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    }, [editor])

    const toggleFullscreen = useCallback(() => {
        setIsFullscreen(!isFullscreen)
    }, [isFullscreen])

    // Fetch posts with pagination
    const { data: postsData, isLoading, error: fetchError } = useQuery({
        queryKey: ['posts', currentPage],
        queryFn: () => getAllPosts({ 
            page: currentPage, 
            limit: 10,
            sortBy: 'created_at',
            sortOrder: 'desc'
        })
    })

    // Create post mutation
    const createPostMutation = useMutation({
        mutationFn: (postData) => createPost(adminUserId, postData),
        onSuccess: () => {
            queryClient.invalidateQueries(['posts'])
            successBtn.current?.click()
            resetForm()
        },
        onError: (error) => {
            setError(error.message)
        }
    })

    // Update post mutation
    const updatePostMutation = useMutation({
        mutationFn: ({ postId, postData }) => updatePost(postId, postData),
        onSuccess: () => {
            queryClient.invalidateQueries(['posts'])
            successBtn.current?.click()
            resetForm()
            setIsCreateMode(true)
            setEditingPost(null)
        },
        onError: (error) => {
            setError(error.message)
        }
    })

    // Delete post mutation
    const deletePostMutation = useMutation({
        mutationFn: deletePost,
        onSuccess: () => {
            queryClient.invalidateQueries(['posts'])
        },
        onError: (error) => {
            setError(error.message)
        }
    })

    const resetForm = () => {
        setFormData({
            title: '',
            excerpt: '',
            featured_image: '',
            slug: '',
            status: 'published',
            tags: []
        })
        editor?.commands.setContent('<p></p>')
        setError('')
    }

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
    }

    const addTag = () => {
        if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, newTag.trim()]
            }))
            setNewTag('')
        }
    }

    const removeTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }))
    }

    const handleSubmit = () => {
        if (!formData.title.trim()) {
            setError('Title is required')
            return
        }

        if (!editor?.getHTML() || editor.getHTML() === '<p></p>' || !editor.getText().trim()) {
            setError('Content is required')
            return
        }

        const postData = {
            ...formData,
            content: editor.getHTML()
        }

        if (isCreateMode) {
            createPostMutation.mutate(postData)
        } else {
            updatePostMutation.mutate({ postId: editingPost.id, postData })
        }
    }

    const handleEdit = (post) => {
        setIsCreateMode(false)
        setEditingPost(post)
        setFormData({
            title: post.title,
            excerpt: post.excerpt || '',
            featured_image: post.featured_image || '',
            slug: post.slug,
            status: post.status,
            tags: post.tags || []
        })
        editor?.commands.setContent(post.content)
    }

    const handleDelete = (postId) => {
        if (window.confirm('Are you sure you want to delete this post?')) {
            deletePostMutation.mutate(postId)
        }
    }

    // Extract posts from the response structure
    const posts = postsData?.data?.posts || []
    const totalPages = postsData?.data?.pagination?.totalPages || 0

    return (
        <div className="admin-page py-5">
            <div className='container'>
                <h1 className='dash-title'>Posts Management</h1>
                <div className="row">
                    {/* Create/Edit Post Form */}
                    <div className="col-12 mt-4">
                        <div className="dash-card">
                            <div className="dash-card-heading">
                                <h2 className="dash-card-title">
                                    {isCreateMode ? 'Create New Post' : 'Edit Post'}
                                </h2>
                                {!isCreateMode && (
                                    <button 
                                        className="dash-card-heading-btn"
                                        onClick={() => {
                                            setIsCreateMode(true)
                                            setEditingPost(null)
                                            resetForm()
                                        }}
                                    >
                                        <i className="fi fi-sr-plus"></i>
                                        Create New
                                    </button>
                                )}
                            </div>

                            <div className="row">
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Title *</label>
                                        <input 
                                            type="text" 
                                            className="dash-form-input"
                                            value={formData.title}
                                            onChange={(e) => handleInputChange('title', e.target.value)}
                                            placeholder="Enter post title"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Slug</label>
                                        <input 
                                            type="text" 
                                            className="dash-form-input"
                                            value={formData.slug}
                                            onChange={(e) => handleInputChange('slug', e.target.value)}
                                            placeholder="auto-generated-from-title"
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Status</label>
                                        <select 
                                            className="dash-form-select"
                                            value={formData.status}
                                            onChange={(e) => handleInputChange('status', e.target.value)}
                                        >
                                            <option value="published">Published</option>
                                            <option value="draft">Draft</option>
                                            <option value="archived">Archived</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-6 mt-3">
                                    <div className="dash-form-group">
                                        <label>Featured Image URL</label>
                                        <input 
                                            type="url" 
                                            className="dash-form-input"
                                            value={formData.featured_image}
                                            onChange={(e) => handleInputChange('featured_image', e.target.value)}
                                            placeholder="https://example.com/image.jpg"
                                        />
                                    </div>
                                </div>
                                <div className="col-12 mt-3">
                                    <div className="dash-form-group">
                                        <label>Excerpt</label>
                                        <textarea 
                                            className="dash-form-textarea"
                                            value={formData.excerpt}
                                            onChange={(e) => handleInputChange('excerpt', e.target.value)}
                                            placeholder="Brief description of the post (max 500 characters)"
                                            maxLength="500"
                                            rows="3"
                                        />
                                    </div>
                                </div>
                                <div className="col-12 mt-3">
                                    <div className="dash-form-group">
                                        <label>Tags</label>
                                        <div className="d-flex gap-2 mb-2">
                                            <input 
                                                type="text" 
                                                className="dash-form-input"
                                                value={newTag}
                                                onChange={(e) => setNewTag(e.target.value)}
                                                placeholder="Add a tag"
                                                onKeyPress={(e) => e.key === 'Enter' && addTag()}
                                            />
                                            <button 
                                                type="button"
                                                className="dash-form-btn"
                                                onClick={addTag}
                                            >
                                                Add
                                            </button>
                                        </div>
                                        <div className="d-flex flex-wrap gap-2">
                                            {formData.tags.map((tag, index) => (
                                                <span key={index} className="badge bg-primary d-flex align-items-center gap-1">
                                                    {tag}
                                                    <i 
                                                        className="fi fi-sr-cross cursor-pointer"
                                                        onClick={() => removeTag(tag)}
                                                        style={{ fontSize: '10px' }}
                                                    ></i>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Rich Text Editor */}
                                <div className="col-12 mt-3">
                                    <div className="dash-form-group">
                                        <label>Content *</label>
                                        
                                        {/* Modern Editor Toolbar */}
                                        {editor && (
                                            <div className={`modern-editor-toolbar ${isFullscreen ? 'fullscreen-toolbar' : ''}`}>
                                                {/* Top Toolbar Row */}
                                                <div className="toolbar-row">
                                                    {/* History Group */}
                                                    <div className="toolbar-group">
                                                        <button
                                                            type="button"
                                                            onClick={() => editor.chain().focus().undo().run()}
                                                            disabled={!editor.can().chain().focus().undo().run()}
                                                            className="toolbar-btn"
                                                            title="Undo"
                                                        >
                                                            <i className="fas fa-undo-alt"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => editor.chain().focus().redo().run()}
                                                            disabled={!editor.can().chain().focus().redo().run()}
                                                            className="toolbar-btn"
                                                            title="Redo"
                                                        >
                                                            <i className="fas fa-redo-alt"></i>
                                                        </button>
                                                    </div>

                                                    {/* Text Formatting Group */}
                                                    <div className="toolbar-group">
                                                        <button
                                                            type="button"
                                                            onClick={() => editor.chain().focus().toggleBold().run()}
                                                            className={`toolbar-btn ${editor.isActive('bold') ? 'active' : ''}`}
                                                            title="Bold"
                                                        >
                                                            <i className="fas fa-bold"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => editor.chain().focus().toggleItalic().run()}
                                                            className={`toolbar-btn ${editor.isActive('italic') ? 'active' : ''}`}
                                                            title="Italic"
                                                        >
                                                            <i className="fas fa-italic"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => editor.chain().focus().toggleUnderline().run()}
                                                            className={`toolbar-btn ${editor.isActive('underline') ? 'active' : ''}`}
                                                            title="Underline"
                                                        >
                                                            <i className="fas fa-underline"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => editor.chain().focus().toggleStrike().run()}
                                                            className={`toolbar-btn ${editor.isActive('strike') ? 'active' : ''}`}
                                                            title="Strikethrough"
                                                        >
                                                            <i className="fas fa-strikethrough"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => editor.chain().focus().toggleCode().run()}
                                                            className={`toolbar-btn ${editor.isActive('code') ? 'active' : ''}`}
                                                            title="Inline Code"
                                                        >
                                                            <i className="fas fa-code"></i>
                                                        </button>
                                                    </div>

                                                    {/* Headings Group */}
                                                    <div className="toolbar-group">
                                                        <select
                                                            value={
                                                                editor.isActive('heading', { level: 1 }) ? 'h1' :
                                                                editor.isActive('heading', { level: 2 }) ? 'h2' :
                                                                editor.isActive('heading', { level: 3 }) ? 'h3' :
                                                                editor.isActive('heading', { level: 4 }) ? 'h4' :
                                                                editor.isActive('heading', { level: 5 }) ? 'h5' :
                                                                editor.isActive('heading', { level: 6 }) ? 'h6' : 'p'
                                                            }
                                                            onChange={(e) => {
                                                                const value = e.target.value
                                                                if (value === 'p') {
                                                                    editor.chain().focus().setParagraph().run()
                                                                } else {
                                                                    const level = parseInt(value.charAt(1))
                                                                    editor.chain().focus().toggleHeading({ level }).run()
                                                                }
                                                            }}
                                                            className="toolbar-select"
                                                        >
                                                            <option value="p">Paragraph</option>
                                                            <option value="h1">Heading 1</option>
                                                            <option value="h2">Heading 2</option>
                                                            <option value="h3">Heading 3</option>
                                                            <option value="h4">Heading 4</option>
                                                            <option value="h5">Heading 5</option>
                                                            <option value="h6">Heading 6</option>
                                                        </select>
                                                    </div>

                                                    {/* Color Tools */}
                                                    <div className="toolbar-group">
                                                        <div className="color-tool-wrapper">
                                                            <button
                                                                type="button"
                                                                onClick={() => setActiveColorTool(activeColorTool === 'text' ? null : 'text')}
                                                                className={`toolbar-btn color-btn ${activeColorTool === 'text' ? 'active' : ''}`}
                                                                title="Text Color"
                                                            >
                                                                <i className="fas fa-font"></i>
                                                                <div className="color-indicator" style={{ backgroundColor: editor.getAttributes('textStyle').color || '#000000' }}></div>
                                                            </button>
                                                            {activeColorTool === 'text' && (
                                                                <div className="color-palette">
                                                                    {['#000000', '#e60000', '#ff9900', '#ffcc00', '#008a00', '#0066cc', '#9933ff', '#ffffff', '#facccc', '#ffebcc', '#ffffcc', '#cce5cc', '#cce5ff', '#e6ccff'].map(color => (
                                                                        <button
                                                                            key={color}
                                                                            type="button"
                                                                            className="color-option"
                                                                            style={{ backgroundColor: color }}
                                                                            onClick={() => {
                                                                                editor.chain().focus().setColor(color).run()
                                                                                setActiveColorTool(null)
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="color-tool-wrapper">
                                                            <button
                                                                type="button"
                                                                onClick={() => setActiveColorTool(activeColorTool === 'highlight' ? null : 'highlight')}
                                                                className={`toolbar-btn color-btn ${activeColorTool === 'highlight' ? 'active' : ''}`}
                                                                title="Highlight Color"
                                                            >
                                                                <i className="fas fa-highlighter"></i>
                                                                <div className="color-indicator" style={{ backgroundColor: editor.getAttributes('highlight').color || '#ffff00' }}></div>
                                                            </button>
                                                            {activeColorTool === 'highlight' && (
                                                                <div className="color-palette">
                                                                    {['#ffff00', '#ffcc00', '#ff9900', '#ff6600', '#ff3300', '#00ff00', '#00cc00', '#009900', '#0099ff', '#0066cc', '#6600cc', '#cc00cc'].map(color => (
                                                                        <button
                                                                            key={color}
                                                                            type="button"
                                                                            className="color-option"
                                                                            style={{ backgroundColor: color }}
                                                                            onClick={() => {
                                                                                editor.chain().focus().toggleHighlight({ color }).run()
                                                                                setActiveColorTool(null)
                                                                            }}
                                                                        />
                                                                    ))}
                                                                    <button
                                                                        type="button"
                                                                        className="color-option remove-highlight"
                                                                        onClick={() => {
                                                                            editor.chain().focus().unsetHighlight().run()
                                                                            setActiveColorTool(null)
                                                                        }}
                                                                        title="Remove Highlight"
                                                                    >
                                                                        <i className="fas fa-times"></i>
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Alignment Group */}
                                                    <div className="toolbar-group">
                                                        <button
                                                            type="button"
                                                            onClick={() => editor.chain().focus().setTextAlign('left').run()}
                                                            className={`toolbar-btn ${editor.isActive({ textAlign: 'left' }) ? 'active' : ''}`}
                                                            title="Align Left"
                                                        >
                                                            <i className="fas fa-align-left"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => editor.chain().focus().setTextAlign('center').run()}
                                                            className={`toolbar-btn ${editor.isActive({ textAlign: 'center' }) ? 'active' : ''}`}
                                                            title="Align Center"
                                                        >
                                                            <i className="fas fa-align-center"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => editor.chain().focus().setTextAlign('right').run()}
                                                            className={`toolbar-btn ${editor.isActive({ textAlign: 'right' }) ? 'active' : ''}`}
                                                            title="Align Right"
                                                        >
                                                            <i className="fas fa-align-right"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                                                            className={`toolbar-btn ${editor.isActive({ textAlign: 'justify' }) ? 'active' : ''}`}
                                                            title="Justify"
                                                        >
                                                            <i className="fas fa-align-justify"></i>
                                                        </button>
                                                    </div>

                                                    {/* Lists Group */}
                                                    <div className="toolbar-group">
                                                        <button
                                                            type="button"
                                                            onClick={() => editor.chain().focus().toggleBulletList().run()}
                                                            className={`toolbar-btn ${editor.isActive('bulletList') ? 'active' : ''}`}
                                                            title="Bullet List"
                                                        >
                                                            <i className="fas fa-list-ul"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => editor.chain().focus().toggleOrderedList().run()}
                                                            className={`toolbar-btn ${editor.isActive('orderedList') ? 'active' : ''}`}
                                                            title="Numbered List"
                                                        >
                                                            <i className="fas fa-list-ol"></i>
                                                        </button>

                                                    </div>

                                                    {/* Insert Group */}
                                                    <div className="toolbar-group">
                                                        <button
                                                            type="button"
                                                            onClick={addLink}
                                                            className={`toolbar-btn ${editor.isActive('link') ? 'active' : ''}`}
                                                            title="Add Link"
                                                        >
                                                            <i className="fas fa-link"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={addImage}
                                                            className="toolbar-btn"
                                                            title="Add Image"
                                                        >
                                                            <i className="fas fa-image"></i>
                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={() => editor.chain().focus().toggleBlockquote().run()}
                                                            className={`toolbar-btn ${editor.isActive('blockquote') ? 'active' : ''}`}
                                                            title="Quote"
                                                        >
                                                            <i className="fas fa-quote-right"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => editor.chain().focus().setHorizontalRule().run()}
                                                            className="toolbar-btn"
                                                            title="Horizontal Rule"
                                                        >
                                                            <i className="fas fa-minus"></i>
                                                        </button>
                                                    </div>

                                                    {/* View Group */}
                                                    <div className="toolbar-group">
                                                        <button
                                                            type="button"
                                                            onClick={toggleFullscreen}
                                                            className="toolbar-btn"
                                                            title="Toggle Fullscreen"
                                                        >
                                                            <i className={`fas ${isFullscreen ? 'fa-compress' : 'fa-expand'}`}></i>
                                                        </button>
                                                    </div>
                                                </div>


                                            </div>
                                        )}
                                        
                                        {/* Editor Container */}
                                        <div className={`modern-editor-container ${isFullscreen ? 'fullscreen-editor' : ''}`}>
                                            <EditorContent editor={editor} />
                                            
                                            {/* Editor Footer */}
                                            <div className="editor-footer">
                                                <div className="editor-stats">
                                                    {editor && (
                                                        <span className="char-count">
                                                            Characters: {editor.getText().length}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="editor-mode-toggle">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const element = document.querySelector('.modern-editor-container')
                                                            element?.classList.toggle('focus-mode')
                                                        }}
                                                        className="focus-mode-btn"
                                                        title="Toggle Focus Mode"
                                                    >
                                                        <i className="fas fa-eye"></i> Focus Mode
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {error && (
                                    <div className="col-12 mt-3">
                                        <Alert severity="error">{error}</Alert>
                                    </div>
                                )}

                                <div className="col-12 mt-3">
                                    <div className="dash-form-btns">
                                        <button 
                                            className="dash-form-btn"
                                            onClick={handleSubmit}
                                            disabled={createPostMutation.isLoading || updatePostMutation.isLoading}
                                        >
                                            {(createPostMutation.isLoading || updatePostMutation.isLoading) && (
                                                <CircularProgress size={20} className="me-2" />
                                            )}
                                            {isCreateMode ? 'Create Post' : 'Update Post'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Posts List */}
                    <div className="col-12 mt-4">
                        <div className="dash-card">
                            <div className="dash-card-heading">
                                <h2 className="dash-card-title">All Posts</h2>
                            </div>

                            {isLoading ? (
                                <div className="d-flex justify-content-center py-5">
                                    <CircularProgress />
                                </div>
                            ) : fetchError ? (
                                <Alert severity="error" className="m-3">
                                    Error loading posts: {fetchError.message}
                                </Alert>
                            ) : (
                                <>
                                    <div className="table-responsive mt-3">
                                        <table className="table dash-table">
                                            <thead>
                                                <tr>
                                                    <td>Title</td>
                                                    <td>Status</td>
                                                    <td>Views</td>
                                                    <td>Likes</td>
                                                    <td>Created</td>
                                                    <td>Actions</td>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {posts.map((post, index) => (
                                                    <tr key={post.id}>
                                                        <td>
                                                            <div className="table-item">
                                                                <h6 className="mb-1">{post.title}</h6>
                                                                {post.excerpt && (
                                                                    <small className="text-muted">
                                                                        {post.excerpt.substring(0, 60)}...
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${
                                                                post.status === 'published' ? 'bg-success' :
                                                                post.status === 'draft' ? 'bg-warning' : 'bg-secondary'
                                                            }`}>
                                                                {post.status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="table-item">{post.views_count || 0}</div>
                                                        </td>
                                                        <td>
                                                            <div className="table-item">{post.likes_count || 0}</div>
                                                        </td>
                                                        <td>
                                                            <div className="table-item">
                                                                {new Date(post.created_at).toLocaleDateString()}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="table-item d-flex">
                                                                <button
                                                                    className="table-action-btn a-blue me-1"
                                                                    onClick={() => handleEdit(post)}
                                                                >
                                                                    <i className="fi fi-rr-pencil"></i>
                                                                </button>
                                                                <button
                                                                    className="table-action-btn a-red"
                                                                    onClick={() => handleDelete(post.id)}
                                                                    disabled={deletePostMutation.isLoading}
                                                                >
                                                                    <i className="fi fi-rr-trash"></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {totalPages > 1 && (
                                        <Pagination 
                                            currentPage={currentPage} 
                                            totalPages={totalPages} 
                                            handlePageChange={setCurrentPage} 
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Success Modal */}
            <button ref={successBtn} data-bs-target="#successModal" data-bs-toggle="modal" hidden></button>
            <div className="modal fade" id="successModal" tabIndex="-1" data-bs-backdrop="static" aria-labelledby="successModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-body success-modal">
                            <button className='success-close-btn btn' data-bs-dismiss="modal" aria-label="Close">
                                <i className="fi fi-rr-cross"></i>
                            </button>
                            <i className="fi fi-rr-check-circle text-success success-icon"></i>
                            <h5>Post {isCreateMode ? 'Created' : 'Updated'} Successfully!</h5>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                /* Modern Editor Toolbar Styles */
                .modern-editor-toolbar {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 12px 12px 0 0;
                    padding: 12px 16px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
                    backdrop-filter: blur(10px);
                    position: relative;
                    overflow: hidden;
                }

                .modern-editor-toolbar::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
                    pointer-events: none;
                }

                .fullscreen-toolbar {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    z-index: 9999;
                    border-radius: 0;
                }

                .toolbar-row {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-wrap: wrap;
                    position: relative;
                    z-index: 1;
                }

                .toolbar-group {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    background: rgba(255, 255, 255, 0.1);
                    padding: 6px;
                    border-radius: 8px;
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                }

                .toolbar-btn {
                    background: rgba(255, 255, 255, 0.1);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    color: white;
                    padding: 8px 10px;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    font-size: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 36px;
                    height: 36px;
                    backdrop-filter: blur(10px);
                    position: relative;
                    overflow: hidden;
                }

                .toolbar-btn::before {
                    content: '';
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    width: 0;
                    height: 0;
                    background: rgba(255, 255, 255, 0.2);
                    border-radius: 50%;
                    transition: all 0.3s ease;
                    transform: translate(-50%, -50%);
                }

                .toolbar-btn:hover::before {
                    width: 100%;
                    height: 100%;
                }

                .toolbar-btn:hover {
                    background: rgba(255, 255, 255, 0.2);
                    border-color: rgba(255, 255, 255, 0.4);
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
                }

                .toolbar-btn:active {
                    transform: translateY(0);
                }

                .toolbar-btn.active {
                    background: rgba(255, 255, 255, 0.3);
                    border-color: rgba(255, 255, 255, 0.5);
                    color: #ffffff;
                    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
                }

                .toolbar-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                    transform: none;
                }

                .toolbar-select {
                    background: rgba(255, 255, 255, 0.1);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    color: white;
                    padding: 8px 12px;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 14px;
                    min-width: 120px;
                    backdrop-filter: blur(10px);
                    transition: all 0.3s ease;
                }

                .toolbar-select:hover {
                    background: rgba(255, 255, 255, 0.2);
                    border-color: rgba(255, 255, 255, 0.4);
                }

                .toolbar-select option {
                    background: #334155;
                    color: white;
                    padding: 8px;
                }

                /* Color Tools */
                .color-tool-wrapper {
                    position: relative;
                }

                .color-btn {
                    position: relative;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 2px;
                }

                .color-indicator {
                    width: 16px;
                    height: 3px;
                    border-radius: 2px;
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    margin-top: 2px;
                }

                .color-palette {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    background: rgba(255, 255, 255, 0.95);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    border-radius: 8px;
                    padding: 8px;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
                    backdrop-filter: blur(10px);
                    z-index: 10;
                    display: grid;
                    grid-template-columns: repeat(7, 1fr);
                    gap: 4px;
                    min-width: 160px;
                    animation: fadeIn 0.3s ease;
                }

                .color-option {
                    width: 20px;
                    height: 20px;
                    border-radius: 4px;
                    border: 2px solid rgba(0, 0, 0, 0.1);
                    cursor: pointer;
                    transition: all 0.2s ease;
                    position: relative;
                }

                .color-option:hover {
                    transform: scale(1.2);
                    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
                }

                .remove-highlight {
                    background: #f8f9fa;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #dc3545;
                    font-size: 10px;
                }

                /* Modern Editor Container */
                .modern-editor-container {
                    position: relative;
                    background: #ffffff;
                    border-radius: 0 0 12px 12px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
                    overflow: hidden;
                    border: 1px solid #e5e7eb;
                    border-top: none;
                    transition: all 0.3s ease;
                }

                .fullscreen-editor {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    z-index: 9998;
                    border-radius: 0;
                    margin-top: 80px;
                }

                .focus-mode {
                    background: #fafafa;
                }

                .focus-mode .ProseMirror {
                    max-width: 800px;
                    margin: 0 auto;
                    font-size: 18px;
                    line-height: 1.8;
                }

                /* Editor Content Styles */
                .modern-editor-content .ProseMirror {
                    min-height: 400px;
                    padding: 24px;
                    border: none;
                    outline: none;
                    font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
                    font-size: 16px;
                    line-height: 1.6;
                    color: #374151;
                    background: transparent;
                    transition: all 0.3s ease;
                }

                .modern-editor-content .ProseMirror:focus {
                    outline: none;
                }

                /* Editor Content Elements */
                .modern-editor-content .ProseMirror h1 {
                    font-size: 2.5rem;
                    font-weight: 700;
                    color: #111827;
                    margin: 1.5rem 0 1rem 0;
                    line-height: 1.2;
                }

                .modern-editor-content .ProseMirror h2 {
                    font-size: 2rem;
                    font-weight: 600;
                    color: #111827;
                    margin: 1.25rem 0 0.75rem 0;
                    line-height: 1.3;
                }

                .modern-editor-content .ProseMirror h3 {
                    font-size: 1.5rem;
                    font-weight: 600;
                    color: #111827;
                    margin: 1rem 0 0.5rem 0;
                }

                .modern-editor-content .ProseMirror p {
                    margin: 0.75rem 0;
                }

                .modern-editor-content .ProseMirror blockquote {
                    border-left: 4px solid #667eea;
                    padding-left: 1.5rem;
                    margin: 1.5rem 0;
                    font-style: italic;
                    background: #f8fafc;
                    padding: 1rem 1rem 1rem 1.5rem;
                    border-radius: 0 8px 8px 0;
                    position: relative;
                }

                .modern-editor-content .ProseMirror blockquote::before {
                    content: '"';
                    position: absolute;
                    top: -10px;
                    left: -10px;
                    font-size: 3rem;
                    color: #667eea;
                    opacity: 0.3;
                    font-family: Georgia, serif;
                }

                .modern-editor-content .ProseMirror ul, 
                .modern-editor-content .ProseMirror ol {
                    padding-left: 1.5rem;
                    margin: 1rem 0;
                }

                .modern-editor-content .ProseMirror li {
                    margin: 0.5rem 0;
                }

                .modern-editor-content .ProseMirror code {
                    background: #f1f5f9;
                    color: #475569;
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
                    font-size: 0.9em;
                    border: 1px solid #e2e8f0;
                }

                .modern-editor-content .ProseMirror pre {
                    background: #1e293b;
                    color: #e2e8f0;
                    padding: 1.5rem;
                    border-radius: 8px;
                    margin: 1.5rem 0;
                    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
                    font-size: 14px;
                    line-height: 1.4;
                    overflow-x: auto;
                    position: relative;
                }

                .modern-editor-content .ProseMirror pre::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 4px;
                    height: 100%;
                    background: #667eea;
                    border-radius: 0 2px 2px 0;
                }

                .modern-editor-content .ProseMirror .editor-image {
                    max-width: 100%;
                    height: auto;
                    border-radius: 8px;
                    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
                    margin: 1.5rem 0;
                    transition: all 0.3s ease;
                    cursor: pointer;
                    animation: fadeInUp 0.5s ease;
                }

                .modern-editor-content .ProseMirror .editor-image:hover {
                    transform: scale(1.02);
                    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
                }

                .modern-editor-content .ProseMirror .link-style {
                    color: #667eea;
                    text-decoration: underline;
                    text-decoration-color: transparent;
                    transition: all 0.3s ease;
                    position: relative;
                }

                .modern-editor-content .ProseMirror .link-style:hover {
                    text-decoration-color: #667eea;
                    color: #5a67d8;
                }

                .modern-editor-content .ProseMirror .link-style::after {
                    content: '';
                    position: absolute;
                    bottom: -2px;
                    left: 0;
                    width: 0;
                    height: 2px;
                    background: #667eea;
                    transition: width 0.3s ease;
                }

                .modern-editor-content .ProseMirror .link-style:hover::after {
                    width: 100%;
                }

                .modern-editor-content .ProseMirror .highlight-style {
                    padding: 2px 4px;
                    border-radius: 4px;
                    font-weight: 500;
                }

                .modern-editor-content .ProseMirror hr {
                    border: none;
                    height: 2px;
                    background: linear-gradient(to right, transparent, #667eea, transparent);
                    margin: 2rem 0;
                }

                /* Placeholder */
                .modern-editor-content .ProseMirror.is-editor-empty:first-child::before {
                    content: attr(data-placeholder);
                    float: left;
                    color: #9ca3af;
                    pointer-events: none;
                    height: 0;
                    font-size: 16px;
                }

                /* Selection styling */
                .modern-editor-content .ProseMirror ::selection {
                    background: rgba(102, 126, 234, 0.2);
                }

                .modern-editor-content .ProseMirror ::-moz-selection {
                    background: rgba(102, 126, 234, 0.2);
                }

                /* Focus styles */
                .modern-editor-content .ProseMirror:focus-within {
                    outline: none;
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                }

                /* Editor Footer */
                .editor-footer {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 12px 24px;
                    background: #f8fafc;
                    border-top: 1px solid #e5e7eb;
                    font-size: 14px;
                }

                .editor-stats {
                    display: flex;
                    gap: 16px;
                    color: #6b7280;
                }

                .editor-stats span {
                    font-weight: 500;
                }

                .focus-mode-btn {
                    background: #667eea;
                    color: white;
                    border: none;
                    padding: 6px 12px;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 12px;
                    font-weight: 500;
                }

                .focus-mode-btn:hover {
                    background: #5a67d8;
                    transform: translateY(-1px);
                }

                /* Responsive Design */
                @media (max-width: 768px) {
                    .toolbar-row {
                        gap: 8px;
                    }
                    
                    .toolbar-group {
                        padding: 4px;
                    }
                    
                    .toolbar-btn {
                        min-width: 32px;
                        height: 32px;
                        padding: 6px 8px;
                    }
                    
                    .toolbar-select {
                        min-width: 100px;
                        padding: 6px 10px;
                    }
                    
                    .modern-editor-content .ProseMirror {
                        padding: 16px;
                        font-size: 15px;
                    }
                    
                    .color-palette {
                        grid-template-columns: repeat(5, 1fr);
                    }

                    .modern-editor-content .ProseMirror h1 {
                        font-size: 1.8rem;
                    }
                    
                    .modern-editor-content .ProseMirror h2 {
                        font-size: 1.5rem;
                    }
                    
                    .modern-editor-content .ProseMirror h3 {
                        font-size: 1.25rem;
                    }
                }

                /* Animation classes */
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                /* Additional Utility Classes */
                .badge {
                    padding: 0.25rem 0.5rem;
                    font-size: 0.75rem;
                }
                
                .cursor-pointer {
                    cursor: pointer;
                }

                /* Print styles */
                @media print {
                    .modern-editor-toolbar,
                    .editor-footer {
                        display: none;
                    }
                    
                    .modern-editor-container {
                        box-shadow: none;
                        border: none;
                    }

                    .modern-editor-content .ProseMirror {
                        font-size: 12pt;
                        line-height: 1.4;
                        color: black;
                    }
                    
                    .modern-editor-content .ProseMirror .editor-image {
                        max-width: 100%;
                        page-break-inside: avoid;
                    }
                    
                    .modern-editor-content .ProseMirror pre {
                        page-break-inside: avoid;
                        background: #f5f5f5 !important;
                        color: black !important;
                        border: 1px solid #ddd;
                    }
                }
            `}</style>
        </div>
    )
}

export default AdminPosts 